import React, { memo, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useAppDispatch } from "../../app/hooks";
import {
  getUserDetailsAsync,
  loginAsync,
  setIsloggedIn,
  setTheme,
  setUser,
} from "../../redux/reducers/appReducer";
import { AuthRequestParams } from "../../redux/stores/appStore";
import { encryptPassword } from "../../helpers/formatter";
import { encryptionKey } from "../../constants/appConstant";
import { liveGageTheme, themeContext } from "../../constants/theme";
import { updateTheme } from "../../helpers/theme";
import { Loader } from "../Loader";
import "../../styles/pages/_login.scss";

const Login = () => {
  const theme = useContext(themeContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [modal, showModal] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [inputType, setInputType] = useState("password");

  const [userDetails, setUserDetails] = useState<AuthRequestParams>({
    userId: "",
    password: "",
  });

  useEffect(() => {
    updateTheme(liveGageTheme);
    dispatch(setTheme(liveGageTheme));
  }, [dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    setUserDetails({ ...userDetails, [field]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!userDetails.userId || !userDetails.password) {
      showModal(true);
      return;
    }

    setLoadingStatus(true);
    dispatch(setUser(userDetails.userId));

    try {
      const encryptedPassword = encryptPassword(userDetails.password, encryptionKey.key);
      const data = {
        username: userDetails.userId,
        password: encryptedPassword,
      };

      const user: any = await dispatch(loginAsync(data));
      if (user.payload?.token) {
        await dispatch(getUserDetailsAsync({ userId: userDetails.userId, password: null }));
        dispatch(setIsloggedIn(true));
        sessionStorage.setItem("jwtToken", user.signInUserSession.accessToken.jwtToken);
        navigate("/");
      } else {
        alert("User not authorised!");
        setLoadingStatus(false);
      }
    } catch (error) {
      console.error(error);
      setLoadingStatus(false);
      alert("Invalid user credentials!");
    }
  };

  const togglePassword = () => {
    setInputType(inputType === "password" ? "text" : "password");
  };

  return (
    <>
      {loadingStatus && <Loader />}

      <div className="login-wrapper min-vh-100 d-flex align-items-center justify-content-center">
        <div className="card-panel">
          {/* Left Panel */}
          <div className="left-panel d-none d-md-flex">
            <img src="/assets/img/LoginImage.svg" alt="Illustration" className="illustration-image-login" />
          </div>

          {/* Right Panel */}
          <div className="right-panel">
            <div className="form">
              <div className="logo">
                <img src="/assets/img/HOMEAID.svg" alt="HomeAid Logo" className="mb-3 logo" />
              </div>

              <div className="welcome-container">
                <p className="mb-0 welcome-text">
                  Welcome Back! Let's unlock your world of possibilities!
                </p>
                <p className="mb-0 pt-2">
                  <span className="new-user">New User? </span>
                  <a href="/register" className="register-text">Register here</a>
                </p>
              </div>

              {/* User ID Input */}
              <div className="floating-input mb-3 d-flex">
                <input
                  type="text"
                  id="userId"
                  value={userDetails.userId}
                  onChange={(e) => handleChange(e, "userId")}
                  placeholder="Enter your User ID"
                  className={!userDetails.userId && modal ? "is-invalid" : ""}
                />
                <label htmlFor="userId">User ID</label>
                <img src="/assets/img/person_icon.svg" alt="User icon" className="floating-icon" />
               
              </div>

              {/* Password Input */}
              <div className="floating-input d-flex">
                <input
                  type={inputType}
                  id="password"
                  value={userDetails.password}
                  onChange={(e) => handleChange(e, "password")}
                  placeholder="Enter your password"
                  className={!userDetails.password && modal ? "is-invalid" : ""}
                />
                <label htmlFor="password">Password</label>
                <img
                  src={`/assets/img/${inputType === "text" ? "eye_icon.svg" : "eye_close_icon.svg"}`}
                  alt="Toggle visibility"
                  className="floating-icon"
                  onClick={togglePassword}
                  style={{ cursor: "pointer" }}
                />
               
              </div>

              {/* Forgot Password Link */}
              <div className="text-end mb-2 forgot">
                <a href="/forgotpassword">Forgot Password?</a>
              </div>

              {/* Login Button */}
              <button className="btn-login" onClick={handleSubmit}>
                Login
              </button>

              {/* Divider */}
              <div className="divider">
                <div className="divider-line"></div>
                <div className="divider-text">
                  <p className="mb-0">Or Login with</p>
                </div>
                <div className="divider-line"></div>
              </div>

              {/* Social Buttons */}
              <div className="social-container">
                <div className="social-btn">
                  <img src="/assets/img/Google_icon.svg" alt="Google login" />
                </div>
                <div className="social-btn">
                  <img src="/assets/img/Apple_icon.svg" alt="Apple login" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(Login);
